import { useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";

import { linkError, linkHadCode, supabase } from "./supabase";

export type Org = { id: string; name: string; role: string; canApprove: boolean };

export type Access =
  | { state: "loading" }
  /** `problem` is set when a link brought them here and could not be finished. */
  | { state: "signed-out"; problem: string | null }
  /** Arrived on a password-reset link. They hold a session, but the only thing
   *  they may do with it is choose a new password. */
  | { state: "recovery" }
  /** Signed in, but this account manages no household — a worker, most likely. */
  | { state: "not-an-employer"; email: string | null }
  /** Signed in, but we could not find out what they may see. Not the same as
   *  signed out, and it must not be reported as such. */
  | { state: "error"; message: string }
  | { state: "ready"; session: Session; orgs: Org[] };

/**
 * Who is signed in, and which households they may act for.
 *
 * The session and the membership lookup are deliberately two separate effects.
 * supabase-js holds an internal lock while an onAuthStateChange callback runs,
 * so awaiting another Supabase call inside that callback can deadlock: the
 * sign-in succeeds, the callback never returns, and the page sits there looking
 * as though the button did nothing. The callback here only ever puts the
 * session into React state; the query happens afterwards, outside the lock.
 *
 * Membership is read from the `memberships` table rather than the `my_org_ids()`
 * RPC on purpose: that function is SECURITY DEFINER, so it answers with the
 * definer's privileges instead of the caller's. Selecting through RLS means the
 * dashboard sees exactly what the policies allow and nothing else.
 */
export function useAccess(): Access {
  const [session, setSession] = useState<Session | null | "unknown">("unknown");
  const [recovering, setRecovering] = useState(false);
  const [access, setAccess] = useState<Access>({ state: "loading" });

  // 1. Track the session. Nothing async against Supabase happens in here.
  //
  // PASSWORD_RECOVERY is raised when the session came from a reset link. The
  // flag is cleared by USER_UPDATED, which is what a successful password change
  // raises — so the new-password screen hands over to the dashboard by itself,
  // with no callback to thread back through here.
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      if (event === "PASSWORD_RECOVERY") setRecovering(true);
      if (event === "USER_UPDATED" || event === "SIGNED_OUT") setRecovering(false);
      setSession(next);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // 2. Resolve what that session may see, well clear of the auth lock.
  useEffect(() => {
    if (session === "unknown") return;
    if (!session) {
      // getSession() waits for the URL to be processed, so by here a code that
      // arrived has already had its chance. Still no session means the exchange
      // failed, and the likeliest reason is worth saying out loud.
      setAccess({
        state: "signed-out",
        problem:
          linkError ??
          (linkHadCode
            ? "That link could not be completed. A reset link only works in the " +
              "browser that asked for it — request another one here and open it " +
              "on this computer."
            : null),
      });
      return;
    }

    // Nothing else is readable until they have chosen a password.
    if (recovering) {
      setAccess({ state: "recovery" });
      return;
    }

    let cancelled = false;
    setAccess({ state: "loading" });

    (async () => {
      const { data, error } = await supabase
        .from("memberships")
        .select("org_id, role, can_approve, orgs(id, name)")
        .returns<
          {
            org_id: string;
            role: string;
            can_approve: boolean;
            orgs: { id: string; name: string } | null;
          }[]
        >();

      if (cancelled) return;

      if (error) {
        // Say what happened. Sending somebody back to the sign-in screen after
        // a successful sign-in is the least useful thing this could do.
        setAccess({ state: "error", message: error.message });
        return;
      }

      const orgs: Org[] = (data ?? [])
        .filter((row) => row.orgs)
        .map((row) => ({
          id: row.org_id,
          name: row.orgs!.name,
          role: row.role,
          canApprove: row.can_approve,
        }));

      setAccess(
        orgs.length === 0
          ? { state: "not-an-employer", email: session.user.email ?? null }
          : { state: "ready", session, orgs },
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [session, recovering]);

  return access;
}
